class DuxShirtOfTheMonth extends HTMLElement {
  connectedCallback() {
    if (this.isInitialized) return;

    this.isInitialized = true;
    this.abortController = new AbortController();
    this.signal = this.abortController.signal;
    this.reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
    this.desktopGallery = window.matchMedia("(min-width: 990px)");

    this.setupGallery();
    this.setupZoomDialog();
    this.setupProductForm();
  }

  disconnectedCallback() {
    this.abortController?.abort();
    window.clearTimeout(this.galleryScrollTimer);
    window.clearTimeout(this.submissionResetTimer);
    this.isInitialized = false;
  }

  setupGallery() {
    this.gallery = this.querySelector(".dux-sotm__gallery");
    this.galleryTrack = this.querySelector("[data-sotm-gallery-track]");
    this.gallerySlides = [...this.querySelectorAll("[data-sotm-gallery-slide]")];
    this.galleryButtons = [...this.querySelectorAll("[data-sotm-gallery-button]")];
    this.galleryPrevious = this.querySelector("[data-sotm-gallery-previous]");
    this.galleryNext = this.querySelector("[data-sotm-gallery-next]");
    this.galleryStatus = this.querySelector("[data-sotm-gallery-status]");

    if (!this.gallery || !this.galleryTrack || this.gallerySlides.length === 0) return;

    const selectedSlide = this.gallerySlides.find((slide) => slide.classList.contains("is-active"));
    this.galleryIndex = Math.max(0, selectedSlide ? this.gallerySlides.indexOf(selectedSlide) : 0);

    this.galleryButtons.forEach((button) => {
      button.addEventListener("click", () => {
        const index = this.getGalleryIndex(button);
        this.selectGalleryImage(index, { announce: true, scroll: true });
      }, { signal: this.signal });
    });

    this.galleryPrevious?.addEventListener("click", () => {
      this.selectGalleryImage(this.galleryIndex - 1, { announce: true, scroll: true });
    }, { signal: this.signal });

    this.galleryNext?.addEventListener("click", () => {
      this.selectGalleryImage(this.galleryIndex + 1, { announce: true, scroll: true });
    }, { signal: this.signal });

    this.gallery.addEventListener("keydown", (event) => this.handleGalleryKeydown(event), { signal: this.signal });
    this.galleryTrack.addEventListener("scroll", () => this.queueGalleryScrollSync(), {
      passive: true,
      signal: this.signal
    });

    const syncForViewport = () => {
      this.syncGalleryAccessibility();
      if (!this.desktopGallery.matches) this.scrollGalleryToIndex(this.galleryIndex, false);
    };
    this.desktopGallery.addEventListener("change", syncForViewport);
    this.signal.addEventListener("abort", () => {
      this.desktopGallery.removeEventListener("change", syncForViewport);
    }, { once: true });

    this.selectGalleryImage(this.galleryIndex, { announce: false, scroll: false });
  }

  getGalleryIndex(element) {
    const parsedIndex = Number.parseInt(element?.dataset.galleryIndex, 10);
    return Number.isInteger(parsedIndex) ? parsedIndex : 0;
  }

  normalizeGalleryIndex(index) {
    const count = this.gallerySlides.length;
    return ((index % count) + count) % count;
  }

  selectGalleryImage(index, { announce = false, scroll = false } = {}) {
    if (!this.gallerySlides?.length) return;

    this.galleryIndex = this.normalizeGalleryIndex(index);
    this.gallerySlides.forEach((slide, slideIndex) => {
      const isActive = slideIndex === this.galleryIndex;
      slide.classList.toggle("is-active", isActive);
      slide.toggleAttribute("data-active", isActive);
    });

    this.galleryButtons.forEach((button) => {
      const isActive = this.getGalleryIndex(button) === this.galleryIndex;
      button.classList.toggle("is-active", isActive);
      button.setAttribute("aria-pressed", isActive.toString());
    });

    this.syncGalleryAccessibility();
    if (scroll && !this.desktopGallery.matches) this.scrollGalleryToIndex(this.galleryIndex, true);
    if (announce) this.announceGalleryImage();
  }

  syncGalleryAccessibility() {
    if (!this.gallerySlides?.length) return;

    this.gallerySlides.forEach((slide, slideIndex) => {
      const hiddenOnDesktop = this.desktopGallery.matches && slideIndex !== this.galleryIndex;
      slide.toggleAttribute("inert", hiddenOnDesktop);
      slide.setAttribute("aria-hidden", hiddenOnDesktop.toString());
    });
  }

  scrollGalleryToIndex(index, animated) {
    const slide = this.gallerySlides[index];
    if (!slide || !this.galleryTrack) return;

    this.galleryTrack.scrollTo({
      behavior: animated && !this.reducedMotion.matches ? "smooth" : "auto",
      left: slide.offsetLeft
    });
  }

  queueGalleryScrollSync() {
    if (this.desktopGallery.matches) return;

    window.clearTimeout(this.galleryScrollTimer);
    this.galleryScrollTimer = window.setTimeout(() => {
      const scrollPosition = this.galleryTrack.scrollLeft;
      const closestIndex = this.gallerySlides.reduce((bestIndex, slide, index) => {
        const currentDistance = Math.abs(this.gallerySlides[bestIndex].offsetLeft - scrollPosition);
        const nextDistance = Math.abs(slide.offsetLeft - scrollPosition);
        return nextDistance < currentDistance ? index : bestIndex;
      }, 0);

      if (closestIndex !== this.galleryIndex) {
        this.selectGalleryImage(closestIndex, { announce: true, scroll: false });
      }
    }, 90);
  }

  handleGalleryKeydown(event) {
    if (event.altKey || event.ctrlKey || event.metaKey) return;
    const galleryButton = event.target instanceof Element
      ? event.target.closest("[data-sotm-gallery-button]")
      : null;
    if (!galleryButton) return;

    let nextIndex;
    if (event.key === "ArrowLeft") nextIndex = this.galleryIndex - 1;
    else if (event.key === "ArrowRight") nextIndex = this.galleryIndex + 1;
    else if (event.key === "Home") nextIndex = 0;
    else if (event.key === "End") nextIndex = this.gallerySlides.length - 1;
    else return;

    event.preventDefault();
    this.selectGalleryImage(nextIndex, { announce: true, scroll: true });
    this.galleryButtons.find((button) => this.getGalleryIndex(button) === this.galleryIndex)?.focus();
  }

  announceGalleryImage() {
    if (!this.galleryStatus) return;

    const activeSlide = this.gallerySlides[this.galleryIndex];
    const label = activeSlide?.dataset.galleryLabel
      || `Image ${this.galleryIndex + 1}`;
    this.galleryStatus.textContent = `${label}, image ${this.galleryIndex + 1} of ${this.gallerySlides.length}.`;
  }

  setupZoomDialog() {
    this.zoomDialog = this.querySelector("[data-sotm-zoom-dialog]");
    this.zoomImage = this.zoomDialog?.querySelector("[data-sotm-zoom-image]");
    this.zoomCaption = this.zoomDialog?.querySelector("[data-sotm-zoom-caption]")
      || this.zoomDialog?.querySelector(".dux-sotm__zoom-caption");
    this.zoomClose = this.zoomDialog?.querySelector("[data-sotm-zoom-close]");
    this.zoomButtons = [...this.querySelectorAll("[data-sotm-zoom-open]")];

    if (!this.zoomDialog || !this.zoomImage || this.zoomButtons.length === 0) return;

    this.zoomButtons.forEach((button) => {
      button.addEventListener("click", () => this.openZoomDialog(button), { signal: this.signal });
    });

    this.zoomClose?.addEventListener("click", (event) => {
      event.preventDefault();
      this.closeZoomDialog();
    }, { signal: this.signal });
    this.zoomDialog.addEventListener("click", (event) => {
      if (event.target === this.zoomDialog) this.closeZoomDialog();
    }, { signal: this.signal });
    this.zoomDialog.addEventListener("keydown", (event) => {
      if (event.key === "Escape" && typeof this.zoomDialog.close !== "function") {
        event.preventDefault();
        this.closeZoomDialog();
      }
    }, { signal: this.signal });
    this.zoomDialog.addEventListener("close", () => {
      this.zoomTrigger?.focus();
      this.zoomTrigger = null;
    }, { signal: this.signal });
  }

  openZoomDialog(button) {
    const slide = button.closest("[data-sotm-gallery-slide]");
    const sourceImage = slide?.querySelector(".dux-sotm__gallery-image");
    const source = button.dataset.zoomSrc
      || button.dataset.fullSrc
      || sourceImage?.dataset.fullSrc
      || sourceImage?.currentSrc
      || sourceImage?.src;
    if (!source) return;

    const imageAlt = button.dataset.fullAlt
      || sourceImage?.alt
      || "Shirt of the Month product image";
    const caption = button.dataset.zoomCaption || imageAlt;

    this.zoomTrigger = button;
    this.zoomImage.src = source;
    this.zoomImage.alt = imageAlt;
    if (this.zoomCaption) this.zoomCaption.textContent = caption;

    if (typeof this.zoomDialog.showModal === "function") this.zoomDialog.showModal();
    else this.zoomDialog.setAttribute("open", "");
  }

  closeZoomDialog() {
    if (typeof this.zoomDialog?.close === "function") {
      if (this.zoomDialog.open) this.zoomDialog.close();
      return;
    }

    this.zoomDialog?.removeAttribute("open");
    this.zoomTrigger?.focus();
    this.zoomTrigger = null;
  }

  setupProductForm() {
    const productData = this.querySelector("[data-sotm-product]");
    this.productForm = this.querySelector(".dux-sotm__form");
    if (!productData || !this.productForm) return;

    try {
      this.product = JSON.parse(productData.textContent);
    } catch (error) {
      return;
    }

    this.optionInputs = [...this.querySelectorAll("[data-sotm-option-input]")];
    this.variantInput = this.querySelector("[data-sotm-variant-id]");
    this.sellingPlanInput = this.querySelector("[data-sotm-selling-plan]");
    this.prices = [...this.querySelectorAll("[data-sotm-price]")];
    this.comparePrices = [...this.querySelectorAll("[data-sotm-compare-price]")];
    this.planName = this.querySelector("[data-sotm-plan-name]");
    this.selectionHint = this.querySelector("[data-sotm-selection-hint]")
      || this.querySelector(".dux-sotm__selection-hint");
    this.submitButton = this.querySelector(".dux-sotm__submit");
    this.submitLabel = this.querySelector("[data-sotm-submit-label]");
    this.formStatus = this.querySelector("[data-sotm-form-status]");
    this.isSubmitting = false;

    // Claim the submission synchronously, before product-form.js reaches its
    // target-phase handler. The actual control lock remains deferred so
    // product-form can capture the selected variant and selling plan first.
    // This closes the small window where a change event could otherwise render
    // the size UI again and make a second add eligible.
    this.productForm.addEventListener("submit", (event) => {
      if (this.isSubmitting
        || this.submitButton?.disabled
        || this.submitButton?.getAttribute("aria-disabled") === "true") {
        event.preventDefault();
        event.stopImmediatePropagation();
        return;
      }

      this.isSubmitting = true;
    }, { capture: true, signal: this.signal });

    // `formdata` fires after product-form.js has captured the enabled variant
    // and selling-plan controls. Locking here avoids the browser's submit-event
    // microtask checkpoint, which can otherwise disable the button before the
    // product-form submit guard runs and prevent /cart/add entirely.
    this.productForm.addEventListener("formdata", () => {
      if (this.isSubmitting) this.beginSubmission();
    }, { signal: this.signal });

    this.optionInputs.forEach((input) => {
      input.addEventListener("change", () => {
        if (this.isSubmitting) return;
        if (this.formStatus) this.formStatus.textContent = "";
        this.submitButton?.classList.remove("is-confirmed", "is-error");
        this.updateSelectedVariant();
      }, { signal: this.signal });
    });

    const announceCartSuccess = () => {
      this.submitButton?.classList.remove("is-loading", "is-error");
      this.submitButton?.classList.add("is-confirmed");
      if (this.submitLabel) this.submitLabel.textContent = this.product.addedLabel || "Added to cart";
      if (this.formStatus) {
        this.formStatus.classList.remove("is-error");
        this.formStatus.classList.add("is-success");
        this.formStatus.textContent = `${this.getSelectedSize()} subscription added to your cart.`;
      }
      this.queueSubmissionReset();
    };

    // product-form emits this as soon as Shopify accepts /cart/add. The later
    // cart-state refresh remains informational, so its failure cannot suppress
    // the customer's confirmation or leave this CTA waiting indefinitely.
    this.addEventListener("on:cart:success", announceCartSuccess, { signal: this.signal });

    this.addEventListener("on:cart:error", (event) => {
      this.submitButton?.classList.remove("is-loading", "is-confirmed");
      this.submitButton?.classList.add("is-error");
      if (this.submitLabel) this.submitLabel.textContent = this.product.retryLabel || "Try again";
      if (this.formStatus) {
        this.formStatus.classList.remove("is-success");
        this.formStatus.classList.add("is-error");
        this.formStatus.textContent = event.detail?.error
          || "The subscription could not be added. Check your size and try again.";
      }
      this.restoreSubmissionControls();

      const selectedOptions = this.getSelectedOptions();
      const selectedVariant = this.product.variants.find((candidate) => (
        candidate.options.length === selectedOptions.length
        && candidate.options.every((option, index) => option === selectedOptions[index])
      ));
      const canRetry = Boolean(selectedVariant?.available && selectedVariant?.sellingPlanId);
      if (this.submitButton) {
        this.submitButton.disabled = !canRetry;
        this.submitButton.setAttribute("aria-disabled", (!canRetry).toString());
      }
      if (!canRetry) this.renderVariant(selectedVariant);
    }, { signal: this.signal });

    const hasIncompleteSelection = this.getSelectedOptions().some((option) => option === null);
    if (hasIncompleteSelection) this.renderIncompleteSelection();
    else this.updateSelectedVariant();
  }

  getSelectedOptions() {
    const optionCount = this.product?.variants?.[0]?.options?.length || 0;
    const selectedOptions = Array(optionCount).fill(null);

    this.optionInputs.forEach((input) => {
      if (input.checked) selectedOptions[Number(input.dataset.optionIndex)] = input.value;
    });

    return selectedOptions;
  }

  updateSelectedVariant() {
    const selectedOptions = this.getSelectedOptions();
    if (selectedOptions.some((option) => option === null)) {
      this.renderIncompleteSelection();
      return;
    }

    const variant = this.product.variants.find((candidate) => (
      candidate.options.length === selectedOptions.length
      && candidate.options.every((option, index) => option === selectedOptions[index])
    ));

    this.renderVariant(variant);
  }

  renderIncompleteSelection() {
    if (this.variantInput) this.variantInput.disabled = true;
    if (this.sellingPlanInput) this.sellingPlanInput.disabled = true;
    this.setSubmitState(false, this.product?.chooseLabel || "Select a size");
    this.updateSelectionHint("Select a size to continue.", false);
  }

  renderVariant(variant) {
    const isAvailable = Boolean(variant?.available && variant?.sellingPlanId);

    if (this.variantInput) {
      this.variantInput.value = variant?.id || "";
      this.variantInput.disabled = !variant;
    }

    if (variant?.sellingPlanId) {
      if (!this.sellingPlanInput) {
        this.sellingPlanInput = document.createElement("input");
        this.sellingPlanInput.type = "hidden";
        this.sellingPlanInput.name = "selling_plan";
        this.sellingPlanInput.dataset.sotmSellingPlan = "";
        this.productForm.append(this.sellingPlanInput);
      }
      this.sellingPlanInput.value = variant.sellingPlanId;
      this.sellingPlanInput.disabled = false;
    } else if (this.sellingPlanInput) {
      this.sellingPlanInput.disabled = true;
    }

    if (variant) {
      this.prices.forEach((price) => {
        price.textContent = variant.price || "";
      });
      this.comparePrices.forEach((comparePrice) => {
        comparePrice.textContent = variant.compareAtPrice || "";
        comparePrice.hidden = !variant.hasCompareAtPrice;
      });
      if (this.planName) this.planName.textContent = variant.sellingPlanName || "";
    }

    const selectedSize = this.getSelectedSize(variant);
    if (isAvailable) {
      this.setSubmitState(true, this.product.addLabel);
      this.updateSelectionHint(`${selectedSize} selected. Ready to subscribe.`, true);
    } else {
      const label = variant?.available ? this.product.planUnavailableLabel : this.product.soldOutLabel;
      this.setSubmitState(false, label);
      this.updateSelectionHint(
        variant?.available ? "Monthly subscription is unavailable for this size." : "This size is sold out.",
        false
      );
    }
  }

  getSelectedSize(variant) {
    const selectedSizeInput = this.optionInputs.find((input) => {
      if (!input.checked) return false;
      const optionName = input.dataset.optionName
        || input.closest("[data-sotm-option]")?.querySelector(".dux-sotm__option-heading > span")?.textContent
        || input.closest("[data-sotm-option]")?.querySelector("legend")?.textContent;
      return optionName?.trim().toLowerCase() === "size";
    });

    return selectedSizeInput?.value || variant?.options?.join(" / ") || "Size";
  }

  setSubmitState(enabled, label) {
    if (this.submitButton) {
      const disabled = this.isSubmitting || !enabled;
      this.submitButton.disabled = disabled;
      this.submitButton.setAttribute("aria-disabled", disabled.toString());
    }
    if (this.submitLabel) this.submitLabel.textContent = label || "Choose options";
  }

  beginSubmission() {
    this.isSubmitting = true;
    this.submitButton?.classList.remove("is-confirmed", "is-error");
    this.submitButton?.classList.add("is-loading");
    this.submitButton?.setAttribute("aria-busy", "true");
    if (this.submitLabel) {
      this.submitLabel.textContent = this.product?.addingLabel || "Adding subscription…";
    }
    if (this.formStatus) {
      this.formStatus.classList.remove("is-success", "is-error");
      this.formStatus.textContent = "Adding your subscription to the cart…";
    }
    this.optionInputs.forEach((input) => {
      input.dataset.sotmWasDisabled = input.disabled.toString();
      input.disabled = true;
    });
    this.setSubmitState(false, this.submitLabel?.textContent);
  }

  queueSubmissionReset() {
    window.clearTimeout(this.submissionResetTimer);
    this.submissionResetTimer = window.setTimeout(() => this.endSubmission(), 1500);
  }

  endSubmission() {
    this.submitButton?.classList.remove("is-loading", "is-confirmed", "is-error");
    this.restoreSubmissionControls();
    this.updateSelectedVariant();
  }

  restoreSubmissionControls() {
    this.isSubmitting = false;
    this.submitButton?.removeAttribute("aria-busy");
    this.optionInputs.forEach((input) => {
      input.disabled = input.dataset.sotmWasDisabled === "true";
      delete input.dataset.sotmWasDisabled;
    });
  }

  updateSelectionHint(message, ready) {
    if (!this.selectionHint) return;
    this.selectionHint.textContent = message;
    this.selectionHint.classList.toggle("is-ready", ready);
  }
}

if (!customElements.get("dux-shirt-of-the-month")) {
  customElements.define("dux-shirt-of-the-month", DuxShirtOfTheMonth);
}
